import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Package } from 'lucide-react'

/**
 * Flatten substrate batches into per-fill rows (multi-format) or single rows (single-format).
 */
function flattenSubstrates(substrates) {
  const rows = []
  for (const batch of substrates) {
    const hasFills = batch.fills?.length > 0
    if (hasFills) {
      for (const fill of batch.fills) {
        if (fill.available > 0) {
          rows.push({
            key: `fill-${fill.id}`,
            type: 'fill',
            batchId: batch.id,
            fillId: fill.id,
            batchCode: batch.batch_code,
            sizeFormat: fill.block_format?.name || batch.block_format || '-',
            available: fill.available,
            weightKg: fill.block_format?.weight_kg || batch.block_format_weight_kg || 4.5
          })
        }
      }
    } else if (batch.available_blocks > 0) {
      rows.push({
        key: `batch-${batch.id}`,
        type: 'batch',
        batchId: batch.id,
        fillId: null,
        batchCode: batch.batch_code,
        sizeFormat: batch.block_format || '-',
        available: batch.available_blocks,
        weightKg: batch.block_format_weight_kg || 4.5
      })
    }
  }
  return rows
}

export default function SubstrateAllocationTable({
  substrates,
  substrateAllocations,
  fillAllocations,
  onSubstrateAllocation,
  onFillAllocation
}) {
  const rows = flattenSubstrates(substrates)

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-content-secondary">
        <Package className="h-8 w-8 text-content-tertiary" />
        <span className="text-sm">No ready substrates available</span>
      </div>
    )
  }

  const getValue = (row) => {
    if (row.type === 'fill') {
      return fillAllocations[row.fillId]?.quantity || ''
    }
    return substrateAllocations[row.batchId] || ''
  }

  const handleChange = (row, rawValue) => {
    const clamped = Math.min(parseInt(rawValue) || 0, row.available)
    if (row.type === 'fill') {
      onFillAllocation(row.fillId, row.batchId, clamped)
    } else {
      onSubstrateAllocation(row.batchId, clamped)
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Batch</TableHead>
          <TableHead>Size / Format</TableHead>
          <TableHead className="text-right">Available</TableHead>
          <TableHead className="text-right w-20">Bags</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(row => (
          <TableRow key={row.key}>
            <TableCell>
              <span className="font-mono text-sm">{row.batchCode}</span>
            </TableCell>
            <TableCell>
              <span className="text-sm">{row.sizeFormat}</span>
            </TableCell>
            <TableCell className="text-right">
              <span className="text-sm">{row.available}</span>
            </TableCell>
            <TableCell className="text-right">
              <Input
                type="number"
                min={0}
                max={row.available}
                value={getValue(row)}
                onChange={(e) => handleChange(row, e.target.value)}
                placeholder="0"
                className="w-20 text-right ml-auto"
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
